import React from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { CartProvider } from "@/contexts/CartContext";
import ProductProvider from "@/contexts/ProductContext";
import { AdminAuthProvider } from '@/contexts/AdminAuthContext'; // Import AdminAuthProvider

// Layouts
import StoreLayout from "@/components/layouts/StoreLayout";

// Store pages
import Index from "./pages/Index";
import Catalog from "./pages/Catalog";
import ProductDetail from "./pages/ProductDetail";
import Checkout from "./pages/Checkout";
import Success from './pages/Success';
import NotFound from './pages/NotFound';

// Import Legal Pages
import TermsOfUse from './pages/legal/TermsOfUse';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import ReturnPolicy from './pages/legal/ReturnPolicy';
import TaxInfo from './pages/legal/TaxInfo';

// Admin pages
import AdminRoutes from './routes/AdminRoutes';

// Dashboard pages
import Dashboard from "./pages/dashboard/Dashboard";
import CustomersPage from "./pages/dashboard/customers";
import { default as DashboardCustomerForm } from "./pages/dashboard/customers/CustomerForm";


// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner
          position="top-right"
          duration={1000}
          closeButton
          richColors
        />
        <CartProvider>
          <ProductProvider>
            {/* BrowserRouter must wrap components needing router context (like AdminAuthProvider) */}
            <BrowserRouter>
              {/* AdminAuthProvider now inside BrowserRouter */}
              <AdminAuthProvider>
                <Routes>
                  {/* Public Store Routes wrapped in StoreLayout */}
                  <Route element={<StoreLayout />}>
                    <Route index element={<Index />} />
                    <Route path="catalog" element={<Catalog />} />
                    <Route path="product/:id" element={<ProductDetail />} />
                    <Route path="checkout" element={<Checkout />} />
                    <Route path="success" element={<Success />} />
                    {/* Catch All moved outside StoreLayout if legal pages are tested outside */}
                     {/* <Route path="*" element={<NotFound />} /> */}
                  </Route>

                  {/* Legal Pages - Moved outside StoreLayout for testing */}
                  <Route path="/legal/terms-of-use" element={<TermsOfUse />} />
                  <Route path="/legal/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/legal/return-policy" element={<ReturnPolicy />} />
                  <Route path="/legal/tax-info" element={<TaxInfo />} />

                  {/* Dashboard Routes - Consider if these need AdminAuth or separate auth */}
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/dashboard/customers" element={<CustomersPage />} />
                  <Route path="/dashboard/customers/new" element={<DashboardCustomerForm />} />
                  <Route path="/dashboard/customers/edit/:id" element={<DashboardCustomerForm />} />

                  {/* Admin Routes */}
                  {/* This route structure now has access to AdminAuthContext */}
                  <Route path="/admin/*" element={<AdminRoutes />} />

                  {/* Catch All - Now at the end, outside specific layouts */}
                  <Route path="*" element={<NotFound />} />

                </Routes>
              </AdminAuthProvider>
            </BrowserRouter>
          </ProductProvider>
        </CartProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
